import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  VbKategorie, VbKueche, VbNshv, VbPumpe, VbVerbraucher, VbVerteiler, VbVorgabe, VerbraucherStand,
} from './elektro/verbraucher';
import {
  ANNAHMEN_HINWEIS, BILANZ_HINWEIS, PDF_HINWEIS, artname, verbraucherStatusText,
} from './elektro/verbraucher';
import { zerlegeBefund } from './elektro/sicherheitstechnik';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Verbraucherschnittstellen (W2-B2, 16.10.2026) — Abschnitt „Netz und Verteilung".
 *
 * Die Seite LIEST `GET …/elektro/verbraucher`: Küchen, Aufzüge, Fahrtreppen, Löschwasser,
 * Entrauchung und Pumpen aus der Leistungsbilanz der Fachplanung, mit Bemessungsstrom, Anlauf,
 * SV-Pflicht und Kabelvorschlag, gegen den Bedarf der Verteiler im Modell gehalten. Gerechnet wird
 * auf dem Server; die Seite schreibt nichts, auch nicht in die Leistungsbilanz.
 *
 * Ein PDF-Knopf (ELT-VBS) und die Excel-Mappe. Der PDF-Abruf legt nur bei geändertem Stand eine neue
 * Berichtsfassung an, sonst die abgelegte (F11) — das steht am Knopf und über der Seite.
 */
export function ElektroVerbraucher() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <VerbraucherAnsicht api={api} projektId={projekt.id} />;
}

function zustandVon(status: string): Zustand {
  switch (status) {
    case 'fehler': return 'fehler';
    case 'warnung': case 'unterdeckt': case 'nicht_im_modell': return 'warnung';
    case 'ok': case 'gedeckt': return 'gut';
    default: return 'info';
  }
}

function Statusmarke({ status }: { status: string }) {
  return <StatusBadge zustand={zustandVon(status)} text={verbraucherStatusText(status)} klein />;
}

function Befundliste({ befunde }: { befunde: string[] }) {
  if (befunde.length === 0) return null;
  return (
    <ul className="liste">
      {befunde.map((b, i) => {
        const { stufe, text } = zerlegeBefund(b);
        const z: Zustand = stufe === 'fehler' ? 'fehler' : stufe === 'warnung' ? 'warnung' : 'info';
        return <li key={`${i}-${text.slice(0, 24)}`}><StatusBadge zustand={z} text={text} /></li>;
      })}
    </ul>
  );
}

function kw(wert: number | null): string {
  return wert === null ? '—' : `${wert.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kW`;
}

export function VerbraucherAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<VerbraucherStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroVerbraucher(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  async function laden(art: 'nachweis' | 'excel') {
    setLaeuft(art);
    try {
      if (art === 'nachweis') await api.elektroVerbraucherNachweisLaden(projektId);
      else await api.elektroVerbraucherListen(projektId);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  }

  const aktionen = (
    <>
      <button type="button" className="m-knopf" disabled={laeuft !== null}
              onClick={() => { void laden('excel'); }}>
        Excel-Mappe
      </button>
      <button type="button" className="m-knopf" disabled={laeuft !== null} title={PDF_HINWEIS}
              onClick={() => { void laden('nachweis'); }}>
        Nachweis als PDF
      </button>
    </>
  );

  return (
    <div className="seite">
      <PageHeader
        titel="Verbraucherschnittstellen"
        beschreibung="Küchen, Aufzüge, Fahrtreppen, Löschwasser, Entrauchung und Pumpen aus der Leistungsbilanz der Fachplanung: Bemessungsstrom, Anlauf, SV-Pflicht und Kabelvorschlag, gegen den Bedarf der Verteiler im Modell. Gerechnet beim Abruf, nur lesend."
        aktionen={aktionen}
      />
      <p className="hinweis">{PDF_HINWEIS}</p>
      {fehler && <ErrorState titel="Die Verbraucherschnittstellen konnten nicht geladen werden." technisch={fehler} />}
      {!fehler && !stand && <LoadingState />}
      {stand && stand.quelllauf === null && (
        <EmptyState titel="Keine Leistungsbilanz der Fachplanung eingelesen"
                    erklaerung="Die Verbraucher kommen aus der Leistungsbilanz der Fachplanung (Werkzeug „bilanz-quelle“). Ohne sie gibt es nichts abzugleichen." />
      )}
      {stand && stand.quelllauf !== null && <Inhalt stand={stand} />}
    </div>
  );
}

function Inhalt({ stand }: { stand: VerbraucherStand }) {
  const k = stand.kennzahlen;
  return (
    <>
      <p className="hinweis">{BILANZ_HINWEIS}</p>
      {k.aufAnnahmen && <p className="hinweis hinweis-warnung">{ANNAHMEN_HINWEIS}</p>}
      <KpiReihe>
        <KpiCard titel="Verbraucher" wert={k.verbraucher} vergleich={`${kw(k.leistungKw)} · Bedarf ${kw(k.bedarfKw)}`} />
        <KpiCard titel="ohne Abgang im Modell" wert={k.ohneModellabgang}
                 zustand={k.ohneModellabgang > 0 ? 'warnung' : 'gut'} zustandText={k.ohneModellabgang > 0 ? 'fehlt' : 'keine'} />
        <KpiCard titel="nicht im Modell abgebildet" wert={kw(k.fehlbetragKw)}
                 vergleich={`${k.verteilerNichtImModell} Verteiler nicht im Modell`} />
        <KpiCard titel="SV-Pflicht offen" wert={k.svOffen} zustand={k.svOffen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.svOffen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Warnungen" wert={k.warnungen} zustand={k.warnungen > 0 ? 'warnung' : 'gut'}
                 zustandText={k.warnungen > 0 ? 'prüfen' : 'keine'} />
        <KpiCard titel="Fehler" wert={k.fehler} zustand={k.fehler > 0 ? 'fehler' : 'gut'}
                 zustandText={k.fehler > 0 ? 'beheben' : 'keine'} />
      </KpiReihe>
      <Kategorien zeilen={stand.kategorien} />
      <Verbraucher zeilen={stand.verbraucher} />
      <Verteiler zeilen={stand.verteiler} />
      <Nshv zeilen={stand.nshv} />
      <Kueche zeilen={stand.kueche} />
      <Pumpen zeilen={stand.feuerloeschpumpen} />
      <Vorgaben zeilen={stand.vorgaben} />
      {stand.befunde.length > 0 && (
        <ContentSection titel="Allgemeine Befunde">
          <Befundliste befunde={stand.befunde} />
        </ContentSection>
      )}
    </>
  );
}

function Kategorien({ zeilen }: { zeilen: VbKategorie[] }) {
  const spalten: Spalte<VbKategorie>[] = [
    { schluessel: 'art', titel: 'Art', wert: (z) => artname(z.kategorie) },
    { schluessel: 'n', titel: 'Verbraucher', art: 'zahl', wert: (z) => z.verbraucher },
    { schluessel: 'p', titel: 'P', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungKw },
    { schluessel: 'pbed', titel: 'P_bed', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.bedarfKw },
    { schluessel: 'sv', titel: 'im SV-Blatt', art: 'zahl', wert: (z) => z.imSvBlatt },
    { schluessel: 'ohnevt', titel: 'ohne Verteiler', art: 'zahl', wert: (z) => z.ohneVerteiler },
    { schluessel: 'vtm', titel: 'Verteiler im Modell', art: 'zahl', wert: (z) => z.verteilerImModell },
    { schluessel: 'abg', titel: 'Abgänge im Modell', art: 'zahl', wert: (z) => z.modellAbgaenge },
    { schluessel: 'pflicht', titel: 'SV-Pflicht', wert: (z) => z.svPflicht },
    { schluessel: 'offen', titel: 'SV offen', art: 'zahl', wert: (z) => z.svOffen },
  ];
  return (
    <ContentSection titel="Verbraucherarten"
                    beschreibung="Die Art folgt der Bezeichnung in der Bilanz der Fachplanung; dieselbe Anlage in mehreren Blättern (Aufzug als AV- und SV-Abgang) zählt einmal.">
      <DataTable name="Verbraucherarten" spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.kategorie} suche={false} dicht />
    </ContentSection>
  );
}

function Verbraucher({ zeilen }: { zeilen: VbVerbraucher[] }) {
  const [nurBefund, setNurBefund] = useState(false);
  const mitBefund = (z: VbVerbraucher) => z.befunde.some((b) => !b.startsWith('HINWEIS'));
  const gezeigt = nurBefund ? zeilen.filter(mitBefund) : zeilen;
  const spalten: Spalte<VbVerbraucher>[] = [
    { schluessel: 'art', titel: 'Art', wert: (z) => artname(z.kategorie) },
    { schluessel: 'bez', titel: 'Verbraucher', wert: (z) => z.bezeichnung },
    { schluessel: 'vt', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'nshv', titel: 'NSHV', wert: (z) => z.nshv },
    { schluessel: 'p', titel: 'P', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungKw },
    { schluessel: 'cos', titel: 'cos φ', art: 'zahl', nachkomma: 2,
      wert: (z) => z.cosPhi, inhalt: (z) => `${z.cosPhi.toLocaleString('de-DE')}${z.cosPhiAusQuelle ? '' : '*'}` },
    { schluessel: 'ib', titel: 'I_B', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.bemessungsstromA },
    { schluessel: 'anlauf', titel: 'Anlauf', wert: (z) => `${z.anlaufart} · ${z.anlauffaktor.toLocaleString('de-DE')}` },
    { schluessel: 'sa', titel: 'S_A', einheit: 'kVA', art: 'zahl', nachkomma: 1, wert: (z) => z.anlaufKva },
    { schluessel: 'sv', titel: 'SV', wert: (z) => (z.svVersorgt ? 'ja' : 'nein') },
    { schluessel: 'nea', titel: 'NEA', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.neaLeistungKw },
    { schluessel: 'kabel', titel: 'Kabelvorschlag', wert: (z) => z.kabelVorschlag },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} /> },
  ];
  return (
    <ContentSection titel="Verbraucher"
                    beschreibung="I_B = P / (√3 · U · cos φ), Anlauf S_A = √3 · U · k_A · I_B (Lastsprung für die NEA). * = cos φ nicht in der Quelle, Rückfallwert. Der Kabelvorschlag ist der kleinste Querschnitt mit I_z ≥ I_B ohne Häufung und Spannungsfall.">
      <label className="klein">
        <input type="checkbox" checked={nurBefund} onChange={(e) => setNurBefund(e.target.checked)} />
        {' '}nur Verbraucher mit Warnung oder Fehler ({zeilen.filter(mitBefund).length} von {zeilen.length})
      </label>
      <DataTable name={`Verbraucher (${gezeigt.length})`} spalten={spalten} zeilen={gezeigt}
                 schluessel={(z) => `${z.kategorie}|${z.bezeichnung}|${z.verteiler ?? ''}|${z.leistungKw}`} dicht />
      {gezeigt.filter((z) => z.befunde.length > 0).map((z) => (
        <div key={`${z.kategorie}|${z.bezeichnung}|${z.verteiler ?? ''}|${z.leistungKw}`} className="karte">
          <h3>{z.bezeichnung}{z.verteiler ? ` (${z.verteiler})` : ''}</h3>
          <Befundliste befunde={z.befunde} />
        </div>
      ))}
    </ContentSection>
  );
}

function Verteiler({ zeilen }: { zeilen: VbVerteiler[] }) {
  const spalten: Spalte<VbVerteiler>[] = [
    { schluessel: 'vt', titel: 'Verteiler', wert: (z) => z.verteiler },
    { schluessel: 'nshv', titel: 'NSHV', wert: (z) => z.nshv },
    { schluessel: 'n', titel: 'Verbraucher', art: 'zahl', wert: (z) => z.verbraucher },
    { schluessel: 'arten', titel: 'Arten', wert: (z) => z.kategorien.split(', ').map(artname).join(', ') },
    { schluessel: 'pq', titel: 'P_bed Quelle', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.verbraucherBedarfKw },
    { schluessel: 'pm', titel: 'P Modell', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.modellBedarfKw },
    { schluessel: 'kreise', titel: 'Kreise Modell', art: 'zahl', wert: (z) => z.modellKreise },
    { schluessel: 'dp', titel: 'ΔP', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.fehlbetragKw },
    { schluessel: 'status', titel: 'Status', art: 'status', wert: (z) => z.status,
      inhalt: (z) => <Statusmarke status={z.status} /> },
  ];
  return (
    <ContentSection titel="Abgleich je Verteiler"
                    beschreibung="ΔP = max(0, Σ P_bed der Quelle − P Modell). P Modell ist der Bedarf des Verteilers aus der Leistungsbilanz des Modells (bilanz-rechnen) — er wird hier nur gelesen.">
      <DataTable name={`Verteiler (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.verteiler} dicht />
    </ContentSection>
  );
}

function Nshv({ zeilen }: { zeilen: VbNshv[] }) {
  const spalten: Spalte<VbNshv>[] = [
    { schluessel: 'nshv', titel: 'NSHV', wert: (z) => z.nshv },
    { schluessel: 'vt', titel: 'Verteiler', art: 'zahl', wert: (z) => z.verteiler },
    { schluessel: 'n', titel: 'Verbraucher', art: 'zahl', wert: (z) => z.verbraucher },
    { schluessel: 'pq', titel: 'P_bed Quelle', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.verbraucherBedarfKw },
    { schluessel: 'pm', titel: 'P Modell', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.modellBedarfKw },
    { schluessel: 'dp', titel: 'ΔP nicht abgebildet', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.fehlbetragKw },
  ];
  return (
    <ContentSection titel="Nicht im Modell abgebildete Verbraucherlast je NSHV"
                    beschreibung="Was die Bilanz des Modells je NSHV zu wenig trägt — als Vorschau, die Bilanz selbst bleibt unverändert.">
      <DataTable name={`NSHV (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => z.nshv} suche={false} dicht />
    </ContentSection>
  );
}

function Kueche({ zeilen }: { zeilen: VbKueche[] }) {
  const spalten: Spalte<VbKueche>[] = [
    { schluessel: 'pb', titel: 'Planbereich', wert: (z) => z.planbereich },
    { schluessel: 'a', titel: 'Anschlüsse', art: 'zahl', wert: (z) => z.anschluesse },
    { schluessel: 'st', titel: 'Stück', art: 'zahl', wert: (z) => z.stueck },
    { schluessel: 'sd', titel: 'SD', art: 'zahl', wert: (z) => z.schuko },
    { schluessel: 'ez', titel: 'EZ', art: 'zahl', wert: (z) => z.einzel },
    { schluessel: 'cee', titel: 'CEE', art: 'zahl', wert: (z) => z.cee },
    { schluessel: 'so', titel: 'sonstige', art: 'zahl', wert: (z) => z.sonstige },
    { schluessel: 'p', titel: 'P', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungKw },
  ];
  return (
    <ContentSection titel="Küchenpaket: Elektro-Anschlüsse je Planbereich (ELT-67)"
                    beschreibung="Aus dem Küchenpaket (Gewerk Elektro). Ob Einzelabgänge oder eine Pauschale je Verteiler gilt, ist offen (Fachfrage ELT-67).">
      {zeilen.length === 0
        ? <p className="klein">Für dieses Projekt liegt kein Küchenpaket vor.</p>
        : <DataTable name={`Planbereiche (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                     schluessel={(z) => z.planbereich} suche={false} dicht />}
    </ContentSection>
  );
}

function Pumpen({ zeilen }: { zeilen: VbPumpe[] }) {
  if (zeilen.length === 0) return null;
  const spalten: Spalte<VbPumpe>[] = [
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ },
    { schluessel: 'familie', titel: 'Familie', wert: (z) => z.familie },
    { schluessel: 'anlage', titel: 'Anlage', wert: (z) => z.anlage },
    { schluessel: 'p', titel: 'P', einheit: 'kW', art: 'zahl', nachkomma: 1, wert: (z) => z.leistungKw },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    { schluessel: 'raum', titel: 'Raum', wert: (z) => z.raumnummer },
  ];
  return (
    <ContentSection titel="Feuerlöschpumpen im Modell"
                    beschreibung="Aus dem Sprinklermodell — zum Abgleich mit dem Löschwasser der Bilanz.">
      <DataTable name={`Pumpen (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.raumnummer ?? ''}|${z.typ ?? ''}|${z.kennung ?? ''}`} suche={false} dicht />
    </ContentSection>
  );
}

function Vorgaben({ zeilen }: { zeilen: VbVorgabe[] }) {
  const spalten: Spalte<VbVorgabe>[] = [
    { schluessel: 'feld', titel: 'Vorgabe', wert: (z) => z.feld },
    { schluessel: 'art', titel: 'Art', wert: (z) => (z.verwendung ? artname(z.verwendung) : '—') },
    { schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts', wert: (z) => z.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (z) => z.einheit },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (z) => (z.rueckfall ? `${z.herkunft} (Rückfall)` : z.herkunft) },
    { schluessel: 'bestaetigt', titel: 'bestätigt', art: 'status', wert: (z) => z.bestaetigt,
      inhalt: (z) => <StatusBadge zustand={z.bestaetigt ? 'gut' : 'warnung'} text={z.bestaetigt ? 'ja' : 'nein'} klein /> },
    { schluessel: 'norm', titel: 'Normbezug', wert: (z) => z.normbezug },
  ];
  return (
    <ContentSection titel="Vorgaben"
                    beschreibung="Gepflegt unter Elektro-Vorgaben (elektro_vorgaben). „Rückfall“ heißt: keine Zeile im Projekt, gerechnet mit dem Wert der Migration 0960.">
      <DataTable name={`Vorgaben (${zeilen.length})`} spalten={spalten} zeilen={zeilen}
                 schluessel={(z) => `${z.feld}|${z.verwendung ?? ''}`} dicht />
    </ContentSection>
  );
}
